/**
 * format.ts — defensive value formatting + domain mapping for widgets (RP-2714).
 * Every helper tolerates undefined/wrong-typed input and returns a safe string
 * (never throws), because widget payloads are untrusted server data.
 */
import moment from 'moment'
import { AGENTS } from '../lib/agents'
import { CopilotWidget } from '../lib/copilotTypes'

/** widget domain → colour + i18n label key, reusing the agents.ts brand colours. */
export interface WidgetDomain {
  key: 'reviews' | 'bookings' | 'payments'
  color: string
  labelKey: string
}

const DOMAINS: Record<WidgetDomain['key'], WidgetDomain> = {
  reviews: { key: 'reviews', color: AGENTS.reviews.color, labelKey: 'genAI.copilot-widget-reviews' },
  bookings: { key: 'bookings', color: AGENTS.bookings.color, labelKey: 'genAI.copilot-widget-bookings' },
  payments: { key: 'payments', color: AGENTS.payments.color, labelKey: 'genAI.copilot-widget-payments' }
}

/** map a widget type ('review_list', 'payment_stats', …) to its domain. */
export const widgetDomain = (type?: string): WidgetDomain => {
  if (typeof type === 'string') {
    if (type.startsWith('review')) return DOMAINS.reviews
    if (type.startsWith('booking')) return DOMAINS.bookings
    if (type.startsWith('payment')) return DOMAINS.payments
  }
  return DOMAINS.reviews
}

/* ---------- safe field access on untrusted item/data blobs ---------- */
export const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined)

export const num = (v: unknown): number | undefined => {
  if (typeof v === 'number' && isFinite(v)) return v
  if (typeof v === 'string' && v.trim() && isFinite(Number(v))) return Number(v)
  return undefined
}

// Nullish fallback without the `??` operator: the module's build toolchain
// (older CRA/webpack) does not transpile `??`/`?.`, and `||` would wrongly
// discard a legitimate 0. Returns `fallback` only when `v` is undefined.
export const orDefault = (v: number | undefined, fallback: number | undefined): number | undefined =>
  v === undefined ? fallback : v

export const items = (w: CopilotWidget): Array<Record<string, unknown>> =>
  Array.isArray(w.items) ? w.items.filter((it) => it && typeof it === 'object') : []

export const data = (w: CopilotWidget): Record<string, unknown> => (w.data && typeof w.data === 'object' ? w.data : {})

/**
 * The effective stats dict for a *_stats widget, tolerant of the backend's two
 * live shapes: some tools return their numbers flat (bookings/reviews:
 * `{ total, by_status }` / `{ count, avg_rating }`), payments nests them one
 * level deep under `.data` (`{ message, data: { total, … }, q }`). When a plain
 * (non-array) object sits at `.data`, we merge it up so BOTH shapes resolve
 * from the same flat lookup; otherwise the top-level dict is already flat.
 */
export const statsData = (w: CopilotWidget): Record<string, unknown> => {
  const d = data(w)
  const inner = d.data
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...d, ...(inner as Record<string, unknown>) }
  }
  return d
}

/* ---------- presentation formatters ---------- */

/** whole-dollar money for headlines; cents when non-integer. Falls back to ''. */
export const money = (v: unknown): string => {
  const n = num(v)
  if (n === undefined) return ''
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(n)
  } catch (e) {
    return `$${n}`
  }
}

/** compact date like "Aug 12"; adds the year when it isn't the current one. '' on bad input. */
export const shortDate = (v: unknown): string => {
  const raw = str(v) || (typeof v === 'number' ? v : undefined)
  if (raw === undefined) return ''
  const m = moment(raw)
  if (!m.isValid()) return ''
  return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY')
}

/** rating clamped to 0–5 whole stars; undefined when unparseable. */
export const starCount = (v: unknown): number | undefined => {
  const n = num(v)
  if (n === undefined) return undefined
  return Math.max(0, Math.min(5, Math.round(n)))
}

/** avg rating shown to one decimal, e.g. "4.6". '' on bad input. */
export const oneDecimal = (v: unknown): string => {
  const n = num(v)
  return n === undefined ? '' : n.toFixed(1)
}

export type StatusTone = 'positive' | 'pending' | 'negative' | 'neutral'

const TONES: Record<string, StatusTone> = {
  paid: 'positive',
  completed: 'positive',
  confirmed: 'positive',
  succeeded: 'positive',
  accepted: 'positive',
  active: 'positive',
  upcoming: 'pending',
  pending: 'pending',
  scheduled: 'pending',
  processing: 'pending',
  reschedule: 'pending',
  incomplete: 'pending',
  sent: 'pending',
  cancelled: 'negative',
  canceled: 'negative',
  failed: 'negative',
  declined: 'negative',
  refunded: 'negative',
  overdue: 'negative',
  'no-show': 'negative',
  no_show: 'negative'
}

const normStatus = (s?: string): string => (s || '').trim().toLowerCase().replace(/\s+/g, '_')

export const statusTone = (status?: string): StatusTone => TONES[normStatus(status)] || 'neutral'

/**
 * Localised status label, e.g. genAI.copilot-widget-status-paid; falls back to
 * a title-cased raw value when the backend sends a status we don't translate.
 */
export const statusLabel = (status: string | undefined, t: (k: string) => string): string => {
  const raw = str(status)
  if (!raw) return ''
  const norm = normStatus(raw)
  const key = `genAI.copilot-widget-status-${norm}`
  const translated = t(key)
  // A missing key echoes back the key, and i18next strips the `genAI.`
  // namespace when it does — so guard against BOTH forms before falling back
  // to a title-cased raw value (otherwise "accepted" rendered the raw key).
  if (translated && translated !== key && translated !== `copilot-widget-status-${norm}`) {
    return translated
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

/** collapse a source token to its canonical UPPER form (drops spaces/underscores/hyphens). */
const normSource = (raw: string): string => raw.toUpperCase().replace(/[\s_-]+/g, '')

/** friendly platform labels — mirrors getPlatformDisplayName in activity-list/index.js. */
const SOURCE_LABELS: Record<string, string> = {
  GOOGLE: 'Google',
  GOOGLEMYBUSINESS: 'Google',
  FACEBOOK: 'Facebook',
  YELP: 'Yelp',
  YELPKNOWLEDGE: 'Yelp',
  UBERALL: 'Uberall'
}

/** true when a review came in directly (first-party), shown as a verified tick, never a word. */
export const isDirectSource = (raw: unknown): boolean => {
  const s = str(raw)
  return s !== undefined && normSource(s) === 'DIRECT'
}

/**
 * Human platform label for a review's raw source. Third-party platforms map to a
 * friendly name; DIRECT returns undefined (the caller shows a verified ✓ instead
 * of the raw word); an unknown non-empty value is title-cased, never the raw
 * shouty string (e.g. "DIRECT"/"GOOGLEMYBUSINESS").
 */
export const reviewSourceLabel = (raw: unknown): string | undefined => {
  const s = str(raw)
  if (s === undefined) return undefined
  const norm = normSource(s)
  if (norm === 'DIRECT') return undefined
  if (SOURCE_LABELS[norm] !== undefined) return SOURCE_LABELS[norm]
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}
